// frontend/src/farm/skyClock.mjs
// Everything about the farm's backdrop that depends on the time of day, as pure functions of
// `t` = seconds into the 240 s day cycle (night 0–60, morning 60–120, day 120–180,
// evening 180–240; state/dayCycle.js). Colours and alphas are keyframed and interpolated, so
// the sky, the sun and moon, the stars and the lighting change continuously instead of
// stepping every 60 s. Pure (node --test): the caller passes the clock inputs in.
export const CYCLE = 240;
export const PHASE_MID = { night: 30, morning: 90, day: 150, evening: 210 };

// Seconds into the cycle. The dev override (T/R hotkeys) and a missing anchor both freeze
// the sky at the middle of a phase.
export function cycleT({ now, anchorIso, devPhase, serverPhase } = {}) {
  if (devPhase) return PHASE_MID[devPhase] ?? PHASE_MID.day;
  if (!anchorIso) return PHASE_MID[serverPhase] ?? PHASE_MID.night;
  const s = ((now - Date.parse(anchorIso)) / 1000) % CYCLE;
  return s < 0 ? s + CYCLE : s;
}

// Which cycle ("night") this is, counted from the anchor: seeds the shooting-star roll.
export function nightIndex({ now, anchorIso } = {}) {
  if (!anchorIso) return 0;
  return Math.floor((now - Date.parse(anchorIso)) / 1000 / CYCLE);
}

const lerp = (a, b, f) => a + (b - a) * f;

export function lerpColor(a, b, f) {
  const ch = (c, s) => (c >> s) & 255;
  const mix = (s) => Math.round(lerp(ch(a, s), ch(b, s), f));
  return (mix(16) << 16) | (mix(8) << 8) | mix(0);
}

// Keyframes [[t, value], …] sorted by t, first at t = 0; the last segment runs to the first
// key again at t = CYCLE.
export function sample(keys, t, mix = lerp) {
  const tt = ((t % CYCLE) + CYCLE) % CYCLE;
  for (let i = 0; i < keys.length; i++) {
    const [t0, v0] = keys[i];
    const last = i === keys.length - 1;
    const t1 = last ? CYCLE : keys[i + 1][0];
    const v1 = last ? keys[0][1] : keys[i + 1][1];
    if (tt >= t0 && tt < t1) return mix(v0, v1, (tt - t0) / (t1 - t0));
  }
  return keys[0][1];
}
const sampleColor = (keys, t) => sample(keys, t, lerpColor);

const SKY_TOP = [
  [0, 0x0b1030], [50, 0x0b1030], [70, 0x3b4a8a], [90, 0x7fb0e6], [120, 0x4a9df0],
  [180, 0x4a9df0], [205, 0x5a8fd8], [222, 0x4a4a9a], [236, 0x141848],
];
const SKY_BOTTOM = [
  [0, 0x1c2a5a], [50, 0x1c2a5a], [70, 0xffb38a], [90, 0xffe2c4], [120, 0xb8e0ff],
  [180, 0xb8e0ff], [205, 0xffc890], [222, 0xff8a5a], [236, 0x3a2f6a],
];
export function skyColours(t) {
  return { top: sampleColor(SKY_TOP, t), bottom: sampleColor(SKY_BOTTOM, t) };
}

// u: 0 = left, 1 = right; v: 0 = horizon, 1 = top of the sky area. Both bodies start and end
// a little BELOW the horizon, so they rise from and set behind the far hills.
const DIP = 0.08;
export function sunPos(t) {
  if (t < 60) return { u: 0, v: -DIP, visible: false };
  const s = (t - 60) / 180;
  return { u: 0.1 + 0.8 * s, v: Math.sin(Math.PI * s) - DIP, visible: true };
}
export function moonPos(t) {
  const m = (((t - 225) % CYCLE) + CYCLE) % CYCLE / 90;
  if (m > 1) return { u: 0, v: -DIP, visible: false };
  return { u: 0.1 + 0.8 * m, v: Math.sin(Math.PI * m) - DIP, visible: true };
}

const STARS = [[0, 1], [60, 1], [90, 0], [210, 0]];
export function starAlpha(t) { return sample(STARS, t); }

const NIGHT_TINT = { far: 0x56608c, mid: 0x4c5680, near: 0x444c74 };
const bandTintKeys = (night) => [
  [0, night], [50, night], [72, 0xffd8c0], [95, 0xffffff], [190, 0xffffff], [215, 0xffc9a0], [236, night],
];
const BAND_TINT = Object.fromEntries(Object.entries(NIGHT_TINT).map(([k, v]) => [k, bandTintKeys(v)]));
const HAZE_ALPHA = { far: 0.35, mid: 0.15, near: 0 };
export function bandLight(t, depth) {
  return {
    tint: sampleColor(BAND_TINT[depth], t),
    haze: skyColours(t).bottom,
    hazeAlpha: HAZE_ALPHA[depth] * (1 - 0.4 * starAlpha(t)),
  };
}

const FARM_COLOR = [[0, 0x8090c0], [50, 0x8090c0], [75, 0xffe0d0], [100, 0xffffff], [190, 0xffffff], [215, 0xffb070], [235, 0x8090c0]];
const FARM_ALPHA = [[0, 0.55], [50, 0.55], [75, 0.18], [100, 0], [190, 0], [215, 0.28], [235, 0.52]];
export function farmLight(t) {
  return { color: sampleColor(FARM_COLOR, t), alpha: sample(FARM_ALPHA, t) };
}

// The distance haze painted over the far lawn (inside the world, below the farm).
export function worldHazeAlpha(t) { return 0.3 * (1 - 0.4 * starAlpha(t)); }
